import { Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';

import { CommonModule } from '@angular/common';
import { SharedModule } from '@app/shared/shared.module';
import { TranslateModule } from '@ngx-translate/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { SelectButtonOptionClickEvent } from 'primeng/selectbutton';
import { SliderChangeEvent } from 'primeng/slider';
import { DropdownChangeEvent } from 'primeng/dropdown';
import { IdCode, LabelCode, LabelDisabledCode } from '@app/shared/interfaces/label-code.interface';
import { FinancialData } from '@app/shared/interfaces/data/financial-data.interface';
import { AppService } from '@app/app.service';
import { FinancialProductMapperService } from '@app/shared/mappers/financial-product-mapper';
import { LoadDataService } from '@app/shared/services/load-data.service';
import { QuotationMapperService } from '@app/shared/mappers/quotation-mapper';
import { QuotationReverseMapperService } from '@app/shared/mappers/quotation-mapper-reverse';

@Component({
    selector: 'app-financial-simulator',
    standalone: true,
    imports: [CommonModule, SharedModule, TranslateModule],
    templateUrl: './financial-simulator.component.html',
    styleUrl: './financial-simulator.component.scss',
})
export class FinancialSimulatorComponent implements OnInit {
    @Input() brand = '';
    @Input() model = '';
    @Input() data!: FinancialData;

    @Output() isOpenedFinancialCart = new EventEmitter<boolean>();

    private financialProductMapperService = inject(FinancialProductMapperService);
    private quotationMapperService = inject(QuotationMapperService);
    private quotationReverseMapperService = inject(QuotationReverseMapperService);
    private loadDataService = inject(LoadDataService);
    private appData = inject(AppService);

    installmentMonthlyGrossAmount = 'core.components.financialCart.financialSimulator.installmentMonthlyGrossAmount';
    downPaymentGrossAmount = 'core.components.financialCart.financialSimulator.downPaymentGrossAmount';
    duration = 'core.components.financialCart.financialSimulator.duration';

    nominalRate = 'core.components.financialCart.financialSimulator.nominalRate';
    aprcRate = 'core.components.financialCart.financialSimulator.aprcRate';
    amountFinanced = 'core.components.financialCart.financialSimulator.amountFinanced';
    totalInterestGrossAmount = 'core.components.financialCart.financialSimulator.totalInterestGrossAmount';
    totalCreditCost = 'core.components.financialCart.financialSimulator.totalCreditCost';
    totalAmountDue = 'core.components.financialCart.financialSimulator.totalAmountDue';

    installmentFirstGrossAmount = 'core.components.financialCart.financialSimulator.installmentFirstGrossAmount';
    installmentLastGrossAmount = 'core.components.financialCart.financialSimulator.installmentLastGrossAmount';
    installmentTotalGrossAmount = 'core.components.financialCart.financialSimulator.installmentTotalGrossAmount';

    installmentAndInsuranceFirstGrossAmount = 'core.components.financialCart.financialSimulator.installmentAndInsuranceFirstGrossAmount';
    installmentAndInsuranceLastGrossAmount = 'core.components.financialCart.financialSimulator.installmentAndInsuranceLastGrossAmount';
    installmentAndInsuranceTotalGrossAmount = 'core.components.financialCart.financialSimulator.installmentAndInsuranceTotalGrossAmount';

    modifyConditionsFinancing = 'core.components.financialCart.financialSimulator.modifyConditionsFinancing';
    multiOptionCreditConditions = 'core.components.financialCart.financialSimulator.multiOptionCreditConditions';
    classicCreditConditions = 'core.components.financialCart.financialSimulator.classicCreditConditions';
    creditDuration = 'core.components.financialCart.financialSimulator.creditDuration';
    annualMileage = 'core.components.financialCart.financialSimulator.annualMileage';
    formalisationCommission = 'core.components.financialCart.financialSimulator.formalisationCommission';
    paymentProtectionInsurance = 'core.components.financialCart.financialSimulator.paymentProtectionInsurance';

    legalText1 = 'core.components.financialCart.financialSimulator.legalText1';
    legalText2 = 'core.components.financialCart.financialSimulator.legalText2';
    legalText3 = 'core.components.financialCart.financialSimulator.legalText3';
    legalText4 = 'core.components.financialCart.financialSimulator.legalText4';
    legalText5 = 'core.components.financialCart.financialSimulator.legalText5';
    legalText6 = 'core.components.financialCart.financialSimulator.legalText6';

    calculateButton = 'common.buttons.calculateButton';
    cancelButton = 'common.buttons.cancelButton';

    cancelButtonDisabled = false;

    saveButtonDisabled = true;

    periods: LabelCode[] = [];

    financialProductOptions: IdCode[] = [];

    commissionIncluded: LabelDisabledCode[] = [
        { label: 'Incluidos en la financiación', code: 'INC', disabled : false },
        { label: 'A pagar en la primera cuota', code: 'NINC', disabled : true  },
    ];

    paymentInsurance: LabelCode[] = [
        { label: 'Incluído', code: 'INC' },
        { label: 'No incluído', code: 'NINC' },
    ];

    formGroup!: FormGroup;

    selectedFinancialProduct!: IdCode;
    selectedCommissionIncluded: LabelCode = { label: 'Incluidos en la financiación', code: 'INC' };
    selectedPaymentInsurance: LabelCode = { label: 'No incluído', code: 'NINC' };

    selectedDuration: any;
    selectedDownPaymentGrossAmount: number = 0;
    selectedAnnualMileage: number = 0;

    durationsRangeMin: number = 0;
    durationsRangeMax: number = 0;
    durationsRangeStep: number = 0;
    durationsRangeDef: number = 0;

    downPaymentGrossAmountRangeMin: number = 0;
    downPaymentGrossAmountRangeMax: number = 0;
    downPaymentGrossAmountRangeStep: number = 0;
    downPaymentGrossAmountRangeDef: number = 0;

    annualMileagesRangeMin: number = 0;
    annualMileagesRangeMax: number = 0;
    annualMileagesRangeStep: number = 0;
    annualMileagesRangeDef: number = 0;

    isMultiCreditSelected: boolean = false;

    today: Date = new Date();

    constructor(private fb: FormBuilder,
        private appService: AppService,
        //private decimalPipe: DecimalPipe
    ) {}

    ngOnInit(): void {
        this.selectedFinancialProduct = {
            id: this.data?.financialProduct?.id,
            name: this.data?.financialProduct?.name,
        };

        this.selectedCommissionIncluded = this.commissionIncluded[0];
        this.selectedPaymentInsurance = this.data?.secureCode ? this.paymentInsurance[0] :  this.paymentInsurance[1];

        this.selectedDownPaymentGrossAmount = this.data?.downPaymentGrossAmount ? this.data.downPaymentGrossAmount : 0;
        this.selectedAnnualMileage = this.data?.annualMileages ? this.data.annualMileages : 0;

        this.fillDataFinancialProduct();
        //this.setValuesForm(this.appData.data.userData);
        this.createForm();
        this.formGroup.controls.commissionIncluded.setValue(this.selectedCommissionIncluded?.code);
        this.formGroup.controls.paymentInsurance.setValue(this.selectedPaymentInsurance?.code);
        this.formIsValid();
    }

    fillDataFinancialProduct(value: any = undefined) {
        if (value != undefined) {
            this.selectedFinancialProduct = value;
            this.data.financialProduct = value;
        }
        if (this.selectedFinancialProduct.id == 'MCM')
            this.isMultiCreditSelected = true;
        else
            this.isMultiCreditSelected = false;

        if (this.data?.financialProducts && this.data.financialProducts.length > 0) {
            this.data.financialProducts.forEach((financialProductOption) => {
                let financialProductOptionAux: IdCode = {
                    id: financialProductOption.id,
                    name: this.decodeISO88591ToUTF8(financialProductOption.description),
                };

                if (value == undefined && financialProductOptionAux.id != 'LEC'  && financialProductOptionAux.id != 'LFP') this.financialProductOptions.push(financialProductOptionAux);

                if (financialProductOption.id == this.selectedFinancialProduct.id) {
                    this.downPaymentGrossAmountRangeMin = financialProductOption.downPaymentGrossAmountRange.minimum;
                    this.downPaymentGrossAmountRangeMax = financialProductOption.downPaymentGrossAmountRange.maximum;
                    this.downPaymentGrossAmountRangeStep = financialProductOption.downPaymentGrossAmountRange.step;
                    this.downPaymentGrossAmountRangeDef = financialProductOption.downPaymentGrossAmountRange.defaultValue;

                    this.durationsRangeMin = financialProductOption.durationsRange.minimum;
                    this.durationsRangeMax = financialProductOption.durationsRange.maximum;
                    this.durationsRangeStep = financialProductOption.durationsRange.step;
                    this.durationsRangeDef = financialProductOption.durationsRange.defaultValue;

                    this.annualMileagesRangeMin = financialProductOption.annualMileagesRange ? financialProductOption.annualMileagesRange.minimum : 0;
                    this.annualMileagesRangeMax = financialProductOption.annualMileagesRange ? financialProductOption.annualMileagesRange.maximum : 0;
                    this.annualMileagesRangeStep = financialProductOption.annualMileagesRange ? financialProductOption.annualMileagesRange.step : 0;
                    this.annualMileagesRangeDef = financialProductOption.annualMileagesRange
                        ? financialProductOption.annualMileagesRange.defaultValue
                        : 0;

                    this.data.secureCode = financialProductOption.cpiProducts[0].id;
                }
            });

            this.periods = [];

            let i = this.durationsRangeMin;
            while (i <= this.durationsRangeMax) {
                let item: LabelCode = {
                    label: '' + i.toString() + ' meses',
                    code: '' + i.toString() + '',
                };

                this.periods.push(item);
                i += this.durationsRangeStep;
            }

        }
        this.periods.forEach((item) => {
            if (this.data.duration.toString() == item.code) {
                this.selectedDuration = item;
                //this.data.duration = item.code;
            }
        });
    }

    createForm() {
        if (this.selectedDuration?.code > this.durationsRangeMax || this.selectedDuration?.code < this.durationsRangeMin)
            this.selectedDuration = null;

        this.formGroup = this.fb.group({
            typeFinancialProduct: new FormControl(this.selectedFinancialProduct ? this.selectedFinancialProduct.id : this.data.financialProduct.id),
            commissionIncluded: new FormControl(this.selectedCommissionIncluded ? this.selectedCommissionIncluded : 'INC'),
            paymentInsurance: new FormControl(this.data?.secureCode ? this.paymentInsurance[0] : this.paymentInsurance[1]),
            duration: new FormControl(this.selectedDuration ? this.selectedDuration : this.durationsRangeDef),
            downPaymentGrossAmount: new FormControl(
                this.selectedDownPaymentGrossAmount ? this.selectedDownPaymentGrossAmount : this.downPaymentGrossAmountRangeDef,
                [Validators.required, Validators.min(this.downPaymentGrossAmountRangeMin), Validators.max(this.downPaymentGrossAmountRangeMax)],
            ),
            downPaymentGrossAmountSlider: new FormControl(
                this.selectedDownPaymentGrossAmount ? this.selectedDownPaymentGrossAmount : this.downPaymentGrossAmountRangeDef,
                [Validators.required, Validators.min(this.downPaymentGrossAmountRangeMin), Validators.max(this.downPaymentGrossAmountRangeMax)],
            ),
            annualMileageSlider: new FormControl(this.selectedAnnualMileage && this.selectedFinancialProduct.id == 'MCM' ? this.selectedAnnualMileage : this.annualMileagesRangeDef, [
                Validators.required,
                Validators.min(this.annualMileagesRangeMin),
                Validators.max(this.annualMileagesRangeMax),
            ]),
        });
    }

    //-------------------------//
    //------EVENT METHODS------//
    //-------------------------//

    onChangeFinancialProduct(event: SelectButtonOptionClickEvent) {
        this.fillDataFinancialProduct(event.option);

        this.createForm();

        //this.formGroup.controls.typeFinancialProduct.setValue(this.selectedFinancialProduct.code);
        this.formIsValid();
    }

    onChangeDurationFinancing(event: DropdownChangeEvent) {
        this.selectedDuration = event.value;
        this.data.duration = this.selectedDuration.code;
        this.formIsValid();
    }

    onChangeDownPaymentGrossAmount(event: Event) {
        this.selectedDownPaymentGrossAmount = Number(this.formGroup.controls.downPaymentGrossAmount.getRawValue());
        this.data.downPaymentGrossAmount = this.selectedDownPaymentGrossAmount;
        this.data.amountFinanced = this.data.price - this.data.downPaymentGrossAmount;
        if(this.data.amountFinanced < 0)
            this.data.amountFinanced = 0;

        this.formGroup.controls.downPaymentGrossAmountSlider.setValue(this.selectedDownPaymentGrossAmount);
        this.formGroup.controls.downPaymentGrossAmount.setValue(this.selectedDownPaymentGrossAmount);
        this.formIsValid();
    }

    onChangeDownPaymentGrossAmountSlider(event: SliderChangeEvent) {
        this.selectedDownPaymentGrossAmount = Number(event.value);
        this.data.downPaymentGrossAmount = this.selectedDownPaymentGrossAmount;
        this.data.amountFinanced = this.data.price - this.data.downPaymentGrossAmount;

        this.formGroup.controls.downPaymentGrossAmount.setValue(this.selectedDownPaymentGrossAmount);
        this.formIsValid();
    }

    onChangeAnnualMileageSlider(event: SliderChangeEvent) {
        this.selectedAnnualMileage = Number(event.value);
        this.data.annualMileages = this.selectedAnnualMileage;
        this.formGroup.controls.annualMileageSlider.setValue(this.selectedAnnualMileage);
        this.formIsValid();
    }

    onChangeCommissionIncluded(event: SelectButtonOptionClickEvent) {
        this.selectedCommissionIncluded = event.option;
        this.formGroup.controls.commissionIncluded.setValue(this.selectedCommissionIncluded?.code);
        this.formIsValid();
    }

    onChangePaymentInsurance(event: SelectButtonOptionClickEvent) {
        this.selectedPaymentInsurance = event?.option ? event.option : '';
        this.formGroup.controls.paymentInsurance.setValue(this.selectedPaymentInsurance?.code);
        this.formIsValid();
    }

    //-------------------------//
    //-------VALIDATION--------//
    //-------------------------//

    formIsValid() {
        this.saveButtonDisabled = !this.formGroup.valid;
    }

    onSubmit(financialSimulatorData: any) {

        const quotationRequest = this.quotationMapperService.transform(this.appData.data);
        this.loadDataService.getConditions(quotationRequest).subscribe({
            next: (value) => {
                const channel = this.appData.data.financialData.channel; //El channel se 'pierde'
                const financialProducts = this.appData.data.financialData.financialProducts;
                this.appData.data.financialData = this.quotationReverseMapperService.transform(value);
                this.appData.data.financialData.financialProducts = financialProducts;
                this.data = this.appData.data.financialData;
                this.appData.data.vehicleData.annualMileage = this.data.annualMileages;

                this.data.amountFinanced = this.data.price - this.data.downPaymentGrossAmount;
                this.appData.data.financialData.channel = channel;

                console.log(this.appData.data.financialData);
            },
            error: (err) => {
                console.error("Service 'getConditions' emitted an error: " + err);
            },
            complete: () => {
                console.log("Service 'getConditions' emitted the complete notification");
            },
        });

        console.log('Datos_guardados: ', financialSimulatorData);
    }

    onClicButtonCancel() {
        this.isOpenedFinancialCart.emit(false);
        console.log('Cerramos_el_simulador');
    }

    decodeISO88591ToUTF8(encodedText: string): string {
        // Convert the ISO-8859-1 encoded string to a Uint8Array
        const uint8Array = new Uint8Array(encodedText.split('').map(char => char.charCodeAt(0)));

        // Decode the Uint8Array to a UTF-8 string
        const decoder = new TextDecoder('utf-8');
        const decodedText = decoder.decode(uint8Array);

        return decodedText;
    }
}
